import React, { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { smsAPI, type SmsTemplate } from '../../services/api';
import { bookingPagesAPI } from '../../services/calendarsService';
import type { BookingPage } from '../../types/calendar';
import { PHONE_EXAMPLE_E164, IS_INDIA } from '../../config/region';

// LT-238a: canonical fields produced by backend SmsService._build_variables.
// Keep in sync with backend routers/sms.py _CANONICAL_VARIABLE_FIELDS.
const CANONICAL_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'lead_name', label: 'Lead name' },
  { value: 'agent_name', label: 'Agent name' },
  { value: 'callback_number', label: 'Callback number' },
  { value: 'appointment_time', label: 'Appointment time' },
];

// Extract unique ##placeholder## tokens from a DLT-registered template body.
const extractDltPlaceholders = (body: string): string[] => {
  const raw = body.match(/##[A-Za-z0-9_]+##/g) || [];
  const seen: string[] = [];
  for (const token of raw) {
    const name = token.slice(2, -2);
    if (seen.indexOf(name) === -1) seen.push(name);
  }
  return seen;
};

// Drop empty mappings so we don't send "var1" → "" to the backend.
const cleanVarMap = (mp: Record<string, string>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(mp)) {
    if (k.trim() && v.trim()) out[k.trim()] = v.trim();
  }
  return out;
};

const SMSTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newDltTemplateId, setNewDltTemplateId] = useState('');
  const [newMsg91TemplateId, setNewMsg91TemplateId] = useState('');
  const [newSenderId, setNewSenderId] = useState('');
  const [newVarMap, setNewVarMap] = useState<Record<string, string>>({});
  // LT-507: optional booking-page binding for the new template.
  const [newBookingPageId, setNewBookingPageId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editDltTemplateId, setEditDltTemplateId] = useState('');
  const [editMsg91TemplateId, setEditMsg91TemplateId] = useState('');
  const [editSenderId, setEditSenderId] = useState('');
  const [editVarMap, setEditVarMap] = useState<Record<string, string>>({});
  const [editBookingPageId, setEditBookingPageId] = useState<string>('');
  const [editOriginalBookingPageId, setEditOriginalBookingPageId] =
    useState<string>('');
  const [showInfoModal, setShowInfoModal] = useState(false);

  // LT-507: workspace booking pages used by the booking-page picker.
  const [bookingPages, setBookingPages] = useState<BookingPage[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      const list = await smsAPI.getTemplates();
      setTemplates(Array.isArray(list) ? list : []);
    } catch (e: any) {
      const status = e?.response?.status;
      const message = status === 401 || status === 403
        ? "You don't have access to this page. Please check your subscription."
        : 'Failed to load templates';
      setToast({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const loadBookingPages = async () => {
    try {
      const list = await bookingPagesAPI.list({ is_active: true });
      setBookingPages(Array.isArray(list) ? list : []);
    } catch (e) {
      setBookingPages([]);
    }
  };

  useEffect(() => {
    load();
    loadBookingPages();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleCreate = async () => {
    if (!newName.trim() || !newBody.trim()) return;
    setSaving(true);
    try {
      const cleanedNewMap = cleanVarMap(newVarMap);
      await smsAPI.createTemplate({
        name: newName.trim(),
        body: newBody.trim(),
        booking_page_id: newBookingPageId || null,
        ...(IS_INDIA
          ? {
              dlt_template_id: newDltTemplateId.trim() || null,
              msg91_template_id: newMsg91TemplateId.trim() || null,
              sender_id: newSenderId.trim() || null,
              variable_map: Object.keys(cleanedNewMap).length ? cleanedNewMap : null,
            }
          : {}),
      });
      setToast({ type: 'success', message: 'Template created' });
      setAddingNew(false);
      setNewName('');
      setNewBody('');
      setNewDltTemplateId('');
      setNewMsg91TemplateId('');
      setNewSenderId('');
      setNewVarMap({});
      setNewBookingPageId('');
      load();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to create template' });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t: SmsTemplate) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditBody(t.body);
    setEditDltTemplateId(t.dlt_template_id || '');
    setEditMsg91TemplateId(t.msg91_template_id || '');
    setEditSenderId(t.sender_id || '');
    setEditVarMap({ ...(t.variable_map || {}) });
    setEditBookingPageId(t.booking_page_id || '');
    setEditOriginalBookingPageId(t.booking_page_id || '');
    setAddingNew(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditBody('');
    setEditDltTemplateId('');
    setEditMsg91TemplateId('');
    setEditSenderId('');
    setEditVarMap({});
    setEditBookingPageId('');
    setEditOriginalBookingPageId('');
  };

  // LT-507 — see EmailTemplatesPage.persistUpdate for rationale.
  const persistUpdate = async (confirmChange: boolean) => {
    if (!editingId) return;
    setSaving(true);
    try {
      const cleanedEditMap = cleanVarMap(editVarMap);
      const bookingPageChanged =
        editBookingPageId !== editOriginalBookingPageId;
      await smsAPI.updateTemplate(editingId, {
        name: editName.trim(),
        body: editBody.trim(),
        ...(IS_INDIA
          ? {
              dlt_template_id: editDltTemplateId.trim() || null,
              msg91_template_id: editMsg91TemplateId.trim() || null,
              sender_id: editSenderId.trim() || null,
              variable_map: Object.keys(cleanedEditMap).length ? cleanedEditMap : null,
            }
          : {}),
        ...(bookingPageChanged
          ? {
              booking_page_id: editBookingPageId || null,
              confirm_change: confirmChange || undefined,
            }
          : {}),
      });
      setToast({ type: 'success', message: 'Template updated' });
      cancelEdit();
      load();
    } catch (e: any) {
      setToast({
        type: 'error',
        message:
          e?.response?.data?.detail || 'Failed to update template',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !editName.trim() || !editBody.trim()) return;
    const oldId = editOriginalBookingPageId;
    const newId = editBookingPageId;
    if (oldId && newId && oldId !== newId) {
      if (
        !window.confirm(
          "Active sequences may use this template — changing the booking page rebinds future bookings to the new calendar/owner. Continue?",
        )
      ) {
        return;
      }
      await persistUpdate(true);
      return;
    }
    await persistUpdate(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await smsAPI.deleteTemplate(id);
      setToast({ type: 'success', message: 'Template deleted' });
      load();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to delete template' });
    }
  };

  return (
    <div className="relative mx-auto px-2 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
      {toast && (
        <div className="fixed top-20 sm:top-24 left-1/2 -translate-x-1/2 z-[9999] max-w-lg w-full mx-4">
          <div className={`p-3 rounded-lg flex items-center justify-between shadow-lg ${toast.type === 'success' ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
            <span className={`text-sm ${toast.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{toast.message}</span>
            <button type="button" onClick={() => setToast(null)} className="text-gray-400 hover:text-white ml-2">×</button>
          </div>
        </div>
      )}

      <div className="mb-4 sm:mb-6 lg:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-slate-50">
              SMS Templates
            </h1>
            <p className="text-gray-300 text-sm sm:text-base lg:text-lg mt-1">Create and manage SMS message templates for your leads</p>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowInfoModal(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-300 hover:bg-cyan-500/20 transition-colors"
            >
              <InformationCircleIcon className="h-4 w-4" /> Template Info
            </button>
            {!addingNew && (
              <button
                type="button"
                onClick={() => { setAddingNew(true); cancelEdit(); }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2 text-sm font-medium text-white hover:from-emerald-600 hover:to-teal-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4" /> New Template
              </button>
            )}
          </div>
        </div>
      </div>

      {addingNew && (
        <div className="mb-6 rounded-xl border border-slate-600/50 bg-slate-800/60 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-white">Create New Template</h3>
          <input
            type="text"
            placeholder="Template name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
          />
          <textarea
            placeholder="Message body..."
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm resize-none"
          />
          {IS_INDIA && (
            <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <p className="text-xs text-amber-300">
                DLT compliance fields — required before this template can send on India. Register the content on the DLT portal first, then the body here must match the approved text exactly.
              </p>
              <input
                type="text"
                placeholder="DLT Template ID (19 digits)"
                value={newDltTemplateId}
                onChange={(e) => setNewDltTemplateId(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <input
                type="text"
                placeholder="MSG91 Template ID (from MSG91 console)"
                value={newMsg91TemplateId}
                onChange={(e) => setNewMsg91TemplateId(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <input
                type="text"
                placeholder="Sender ID override (leave blank to use deployment default)"
                maxLength={11}
                value={newSenderId}
                onChange={(e) => setNewSenderId(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
              {(() => {
                const detected = extractDltPlaceholders(newBody);
                if (detected.length === 0) {
                  return (
                    <p className="text-[11px] text-amber-300/70 italic">
                      No <code className="font-mono">##placeholder##</code> tokens detected in the body. If your DLT template has them, add matching ones here first.
                    </p>
                  );
                }
                return (
                  <div className="space-y-1.5">
                    <p className="text-[11px] text-amber-300/80">
                      Map each DLT placeholder to the field our system substitutes at send time.
                    </p>
                    {detected.map((ph) => (
                      <div key={ph} className="flex items-center gap-2">
                        <code className="font-mono text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-1 min-w-[120px] text-center">##{ph}##</code>
                        <span className="text-amber-400 text-xs">→</span>
                        <select
                          value={newVarMap[ph] || ''}
                          onChange={(e) => setNewVarMap({ ...newVarMap, [ph]: e.target.value })}
                          className="flex-1 px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">— not mapped —</option>
                          {CANONICAL_FIELDS.map((f) => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
          <BookingPagePicker
            value={newBookingPageId}
            onChange={setNewBookingPageId}
            bookingPages={bookingPages}
            testId="new-sms-template-booking-page"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setAddingNew(false);
                setNewName('');
                setNewBody('');
                setNewDltTemplateId('');
                setNewMsg91TemplateId('');
                setNewSenderId('');
                setNewVarMap({});
                setNewBookingPageId('');
              }}
              className="px-3 py-1.5 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving || !newName.trim() || !newBody.trim()}
              className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-colors text-sm disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-12">Loading templates...</div>
      ) : templates.length === 0 && !addingNew ? (
        <div className="text-center py-16">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-slate-600" />
          <h3 className="mt-3 text-lg font-medium text-slate-300">No SMS templates yet</h3>
          <p className="mt-1 text-sm text-slate-500">Create a template to start sending SMS to your leads.</p>
          <button
            type="button"
            onClick={() => setAddingNew(true)}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2 text-sm font-medium text-white hover:from-emerald-600 hover:to-teal-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" /> Create First Template
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => {
            const isEditing = editingId === t.id;
            return (
              <div
                key={t.id}
                className="rounded-xl border border-slate-600/50 bg-slate-800/60 p-4 transition-colors hover:border-slate-500/50"
              >
                {isEditing ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    />
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm resize-none"
                    />
                    {IS_INDIA && (
                      <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                        <p className="text-xs text-amber-300">
                          DLT compliance — body must match DLT-approved content exactly.
                        </p>
                        <input
                          type="text"
                          placeholder="DLT Template ID (19 digits)"
                          value={editDltTemplateId}
                          onChange={(e) => setEditDltTemplateId(e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="MSG91 Template ID"
                          value={editMsg91TemplateId}
                          onChange={(e) => setEditMsg91TemplateId(e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Sender ID override (leave blank to use deployment default)"
                          maxLength={11}
                          value={editSenderId}
                          onChange={(e) => setEditSenderId(e.target.value.toUpperCase())}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                        />
                        {(() => {
                          const detected = extractDltPlaceholders(editBody);
                          if (detected.length === 0) {
                            return (
                              <p className="text-[11px] text-amber-300/70 italic">
                                No <code className="font-mono">##placeholder##</code> tokens detected in the body.
                              </p>
                            );
                          }
                          return (
                            <div className="space-y-1.5">
                              <p className="text-[11px] text-amber-300/80">
                                Map each DLT placeholder to the field our system substitutes at send time.
                              </p>
                              {detected.map((ph) => (
                                <div key={ph} className="flex items-center gap-2">
                                  <code className="font-mono text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-1 min-w-[120px] text-center">##{ph}##</code>
                                  <span className="text-amber-400 text-xs">→</span>
                                  <select
                                    value={editVarMap[ph] || ''}
                                    onChange={(e) => setEditVarMap({ ...editVarMap, [ph]: e.target.value })}
                                    className="flex-1 px-2 py-1 bg-slate-900 border border-white/10 rounded text-white text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  >
                                    <option value="">— not mapped —</option>
                                    {CANONICAL_FIELDS.map((f) => (
                                      <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                  </select>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    <BookingPagePicker
                      value={editBookingPageId}
                      onChange={setEditBookingPageId}
                      bookingPages={bookingPages}
                      testId={`edit-sms-template-booking-page-${t.id}`}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                        title="Cancel"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={handleUpdate}
                        disabled={saving || !editName.trim() || !editBody.trim()}
                        className="p-1.5 text-green-400 hover:text-green-300 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                        title="Save"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-white truncate">{t.name}</h3>
                      {t.booking_page_id && (
                        <p className="mt-1 text-[11px] text-emerald-300 font-mono">
                          Booking-bearing · books against{' '}
                          {bookingPages.find((p) => p.id === t.booking_page_id)?.slug
                            || bookingPages.find((p) => p.id === t.booking_page_id)?.title
                            || t.booking_page_id}
                        </p>
                      )}
                      <p className="mt-1 text-sm text-slate-400 whitespace-pre-wrap line-clamp-3">{t.body}</p>
                      {IS_INDIA && (
                        <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                          {t.sender_id && (
                            <span className="rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 px-1.5 py-0.5 font-mono">
                              Sender override: {t.sender_id}
                            </span>
                          )}
                          {t.dlt_template_id && (
                            <span className="rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 px-1.5 py-0.5 font-mono">
                              DLT: {t.dlt_template_id}
                            </span>
                          )}
                          {t.msg91_template_id && (
                            <span className="rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 px-1.5 py-0.5 font-mono">
                              MSG91: {t.msg91_template_id}
                            </span>
                          )}
                          {(!t.dlt_template_id || !t.msg91_template_id) && (
                            <span className="rounded bg-red-500/10 border border-red-500/30 text-red-300 px-1.5 py-0.5">
                              DLT fields incomplete — template cannot send
                            </span>
                          )}
                        </div>
                      )}
                      <p className="mt-2 text-xs text-slate-500">
                        Created {new Date(t.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(t)}
                        className="p-1.5 text-cyan-400 hover:text-cyan-300 rounded-lg hover:bg-white/10 transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(t.id)}
                        className="p-1.5 text-red-400 hover:text-red-300 rounded-lg hover:bg-white/10 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showInfoModal && (
        <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={() => setShowInfoModal(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="w-6 h-6 text-cyan-400" />
                <h2 className="text-lg font-semibold text-white">Template Placeholders</h2>
              </div>
              <button type="button" onClick={() => setShowInfoModal(false)} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors" aria-label="Close">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-gray-300">
                Use the following placeholders in your template body. They will be automatically replaced with the actual values when the SMS is sent.
              </p>
              <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600/50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-cyan-400 uppercase">Placeholder</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-cyan-400 uppercase">Replaced with</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-600/30">
                    <tr>
                      <td className="px-4 py-2.5"><code className="rounded bg-slate-600/50 px-1.5 py-0.5 text-emerald-300 text-xs font-mono">{'{{lead_name}}'}</code></td>
                      <td className="px-4 py-2.5 text-gray-300">The lead's full name</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><code className="rounded bg-slate-600/50 px-1.5 py-0.5 text-emerald-300 text-xs font-mono">{'{{agent_name}}'}</code></td>
                      <td className="px-4 py-2.5 text-gray-300">Your name (the logged-in user)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><code className="rounded bg-slate-600/50 px-1.5 py-0.5 text-emerald-300 text-xs font-mono">{'{{callback_number}}'}</code></td>
                      <td className="px-4 py-2.5 text-gray-300">Your assigned outbound phone number</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><code className="rounded bg-slate-600/50 px-1.5 py-0.5 text-emerald-300 text-xs font-mono">{'{{appointment_time}}'}</code></td>
                      <td className="px-4 py-2.5 text-gray-300">Scheduled appointment date/time (if set)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-3">
                <p className="text-xs font-medium text-gray-400 mb-1.5">Example</p>
                <p className="text-sm text-gray-200 font-mono whitespace-pre-wrap">Hi {'{{lead_name}}'}, this is {'{{agent_name}}'}. Call me back on {'{{callback_number}}'}.</p>
                <p className="text-xs text-gray-400 mt-2 italic">Sends as: Hi Sarah Chen, this is Lokesh. Call me back on {PHONE_EXAMPLE_E164}.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowInfoModal(false)}
                className="w-full px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors text-sm"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// LT-507 — booking-page picker shared by the create + edit forms. Same
// shape as the email template page's variant; duplicated rather than
// hoisted to /components to keep the LT-507 footprint local.
interface BookingPagePickerProps {
  value: string;
  onChange: (id: string) => void;
  bookingPages: BookingPage[];
  testId: string;
}

const BookingPagePicker: React.FC<BookingPagePickerProps> = ({
  value,
  onChange,
  bookingPages,
  testId,
}) => (
  <div className="rounded-lg border border-slate-600/40 bg-slate-900/40 p-3">
    <label className="block text-xs text-slate-300">
      <span className="font-mono">Booking page (optional)</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1.5 w-full px-2 py-1.5 bg-slate-900 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
      >
        <option value="">— Regular template (no booking page) —</option>
        {bookingPages.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title || p.slug || p.id}
          </option>
        ))}
      </select>
    </label>
    <p className="mt-1.5 text-[11px] text-slate-500">
      Booking-bearing templates can only be picked by the Book-Meeting
      sequence step. Regular templates are used by SMS steps.
    </p>
  </div>
);

export default SMSTemplatesPage;
